import { HttpBackend, HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { firstValueFrom } from 'rxjs';
import { environment } from '@env/environment';

@Injectable({ providedIn: 'root' })
export class SessionService {
  readonly connectionError = signal<string | null>(null);
  private readonly oauth = inject(OAuthService);
  // Session checks must not trigger the global 401 interceptor's refresh pipeline.
  private readonly http = new HttpClient(inject(HttpBackend));
  private timer?: ReturnType<typeof setInterval>;
  private checking = false;
  private closed = false;
  private loggingOut = false;
  private refreshPromise?: Promise<unknown>;

  refresh(): Promise<unknown> {
    if (!this.refreshPromise) {
      this.refreshPromise = this.oauth.refreshToken().finally(() => { this.refreshPromise = undefined; });
    }
    return this.refreshPromise;
  }


  start(): void {
    if (this.timer || this.closed) return;
    this.timer = setInterval(() => void this.check(), 25000);
    document.addEventListener('visibilitychange', this.onVisible);
    window.addEventListener('focus', this.onFocus);
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = undefined;
    document.removeEventListener('visibilitychange', this.onVisible);
    window.removeEventListener('focus', this.onFocus);
  }

  private readonly onVisible = () => {
    if (!document.hidden) void this.check();
  };
  private readonly onFocus = () => void this.check();

  private async token(): Promise<string> {
    if (!this.oauth.hasValidAccessToken()) {
      if (!this.oauth.getRefreshToken()) throw new Error('No hay refresh token');
      await this.refresh();
    }
    const access = this.oauth.getAccessToken();
    if (!access) throw new Error('No hay access token');
    return access;
  }

  private async request(method: 'GET' | 'POST', endpoint: string, token: string): Promise<void> {
    await firstValueFrom(this.http.request<void>(method, `${environment.API_AUTH_DOMAIN}${endpoint}`, {
      headers: new HttpHeaders({ Authorization: `Bearer ${token}` }),
      withCredentials: method === 'POST',
    }));
  }

  async check(): Promise<void> {
    if (this.checking || this.closed || this.loggingOut) return;
    this.checking = true;
    try {
      if (this.oauth.getAccessToken() && !this.oauth.hasValidAccessToken() && !this.oauth.getRefreshToken()) {
        this.closeLocally();
        return;
      }
      const access = await this.token();
      try {
        await this.request('GET', '/op/session', access);
        this.connectionError.set(null);
      } catch (error) {
        if (isSessionRevoked(error)) throw error;
        if (!(error instanceof HttpErrorResponse) || error.status !== 401) throw error;
        if (!this.oauth.getRefreshToken()) throw error;
        await this.refresh();
        try {
          await this.request('GET', '/op/session', this.oauth.getAccessToken());
          this.connectionError.set(null);
        } catch (retryError) {
          if (isSessionRevoked(retryError)) throw retryError;
          this.connectionError.set('No se pudo verificar la sesión. Reintentaremos automáticamente.');
        }
      }
    } catch (error) {
      if (isSessionRevoked(error) || isTerminalAuthFailure(error)) this.closeLocally();
      else this.connectionError.set('No se pudo verificar la sesión. Reintentaremos automáticamente.');
    } finally {
      this.checking = false;
    }
  }

  closeLocally(): void {
    if (this.closed || this.loggingOut) return;
    this.closed = true;
    this.stop();
    this.oauth.logOut(true);
    this.oauth.initCodeFlow();
  }

  async logout(): Promise<void> {
    if (this.loggingOut) return;
    this.loggingOut = true;
    this.stop();
    try {
      const access = await this.token();
      await this.request('POST', '/op/logout', access);
      this.closed = true;
      this.oauth.logOut(true);
      this.oauth.initCodeFlow();
    } catch (error) {
      this.start();
      throw error;
    } finally {
      this.loggingOut = false;
    }
  }
}

function isSessionRevoked(error: unknown): boolean {
  return error instanceof HttpErrorResponse && error.status === 401 &&
    error.error?.error === 'session_revoked';
}

function isTerminalAuthFailure(error: unknown): boolean {
  return error instanceof HttpErrorResponse &&
    (error.status === 401 || error.status === 400 &&
      (error.error?.error === 'invalid_grant' || error.error?.error === 'session_revoked'));
}
